import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { Filter, Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const INITIAL_LIMIT = 10;
const LOAD_MORE_LIMIT = 5;

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    subcategory: searchParams.get('subcategory') || '',
    minPrice: '',
    maxPrice: '',
    search: searchParams.get('search') || '',
    sortBy: 'newest'
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const cat = searchParams.get('category') || '';
    const srch = searchParams.get('search') || '';
    const subcat = searchParams.get('subcategory') || '';
    if (cat !== filters.category || subcat !== filters.subcategory || srch !== filters.search) {
      setFilters(prev => ({ ...prev, category: cat, subcategory: subcat, search: srch }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setProducts([]);
      setHasMore(true);
      fetchProducts(0, true, INITIAL_LIMIT);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const handleScroll = () => {
      const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 700;
      if (!nearBottom || loading || loadingMore || !hasMore) return;
      fetchProducts(products.length, false, LOAD_MORE_LIMIT);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadingMore, hasMore, products.length, filters]);

  const fetchCategories = async () => {
    try {
      const [catRes, subRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/categories`),
        axios.get(`${BACKEND_URL}/api/subcategories`)
      ]);
      setCategories(catRes.data);
      setSubcategories(subRes.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async (offset = 0, reset = true, pageLimit = INITIAL_LIMIT) => {
    try {
      if (reset) setLoading(true);
      else setLoadingMore(true);
      const params = new URLSearchParams();
      if (filters.category) params.append('category_id', filters.category);
      if (filters.subcategory) params.append('subcategory_id', filters.subcategory);
      if (filters.minPrice) params.append('min_price', filters.minPrice);
      if (filters.maxPrice) params.append('max_price', filters.maxPrice);
      if (filters.search) params.append('search', filters.search);
      if (searchParams.get('featured')) params.append('featured', 'true');
      params.append('sort_by', filters.sortBy);
      params.append('skip', String(offset));
      params.append('limit', String(pageLimit));

      const response = await axios.get(`${BACKEND_URL}/api/products?${params.toString()}`);
      setProducts(prev => reset ? response.data : [...prev, ...response.data]);
      setHasMore(response.data.length === pageLimit);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      if (reset) setLoading(false);
      else setLoadingMore(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      ...(key === 'category' ? { subcategory: '' } : {})
    }));
  };

  const clearFilters = () => {
    setFilters({ category: '', subcategory: '', minPrice: '', maxPrice: '', search: '', sortBy: 'newest' });
  };

  const availableSubcategories = subcategories.filter(s => s.category_id === filters.category);

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12 space-y-4">
          <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white" data-testid="products-page-title">
            {searchParams.get('featured') ? 'Featured Collection' : 'Our Collection'}
          </h1>
          <p className="text-[#A3A3A3]">{products.length} pieces</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <aside className="lg:w-64 space-y-6">
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className="lg:hidden flex items-center space-x-2 text-white"
              data-testid="filter-toggle-button"
            >
              <Filter size={20} /><span>Filters</span>
            </button>

            <div className={`${filterOpen ? 'block' : 'hidden'} lg:block space-y-6 bg-[#141414] border border-white/10 p-6`}>
              <div className="flex items-center justify-between">
                <h3 className="text-sm uppercase tracking-wider text-white">Filters</h3>
                <button onClick={clearFilters} className="text-xs text-[#D4AF37] hover:text-[#F1E5AC]" data-testid="clear-filters-button">
                  Clear All
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-3">Search</label>
                  <div className="relative">
                    <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white pl-6 pr-0 py-2 outline-none transition-colors"
                      data-testid="search-filter-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-3">Category</label>
                  <div className="space-y-2">
                    <button
                      onClick={() => handleFilterChange('category', '')}
                      className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                        !filters.category ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-[#A3A3A3] hover:text-white hover:bg-white/5'
                      }`}
                      data-testid="category-filter-all"
                    >
                      All Categories
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => handleFilterChange('category', cat.id)}
                        className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                          filters.category === cat.id ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-[#A3A3A3] hover:text-white hover:bg-white/5'
                        }`}
                        data-testid={`category-filter-${cat.id}`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-3">Price Range</label>
                  <div className="space-y-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filters.minPrice}
                      onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white px-0 py-2 outline-none transition-colors"
                      data-testid="min-price-filter-input"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={filters.maxPrice}
                      onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white px-0 py-2 outline-none transition-colors"
                      data-testid="max-price-filter-input"
                    />
                  </div>
                </div>

                {filters.category && availableSubcategories.length > 0 && (
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-3">Subcategory</label>
                    <div className="space-y-2">
                      <button
                        onClick={() => handleFilterChange('subcategory', '')}
                        className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                          !filters.subcategory ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-[#A3A3A3] hover:text-white hover:bg-white/5'
                        }`}
                        data-testid="subcategory-filter-all"
                      >
                        All Subcategories
                      </button>
                      {availableSubcategories.map(sub => (
                        <button
                          key={sub.id}
                          onClick={() => handleFilterChange('subcategory', sub.id)}
                          className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                            filters.subcategory === sub.id ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-[#A3A3A3] hover:text-white hover:bg-white/5'
                          }`}
                          data-testid={`subcategory-filter-${sub.id}`}
                        >
                          {sub.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-3">Sort By</label>
                  <div className="space-y-2">
                    {[
                      { value: 'newest', label: 'Newest' },
                      { value: 'price-low', label: 'Price: Low to High' },
                      { value: 'price-high', label: 'Price: High to Low' },
                      { value: 'name', label: 'Name A-Z' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => handleFilterChange('sortBy', opt.value)}
                        className={`block w-full text-left px-3 py-2 text-sm transition-colors ${
                          filters.sortBy === opt.value ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-[#A3A3A3] hover:text-white hover:bg-white/5'
                        }`}
                        data-testid={`sort-by-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <main className="flex-1">
            {loading ? (
              <div className="text-center py-20">
                <p className="text-[#A3A3A3]">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20" data-testid="no-products-message">
                <p className="text-[#A3A3A3] text-lg">No products found</p>
                <button onClick={clearFilters} className="mt-4 text-[#D4AF37] hover:text-[#F1E5AC] text-sm uppercase tracking-wider" data-testid="clear-filters-inline">
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
                {products.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
            {loadingMore && <p className="text-center py-8 text-[#A3A3A3]">Loading more products...</p>}
            {!loading && !loadingMore && products.length > 0 && !hasMore && (
              <p className="text-center py-8 text-[#A3A3A3]">You have reached the end</p>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
